import * as MoneyTransactionsAction from './money-transactions.actions';

import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {MoneyTransaction, MoneyTransactionAccountType, MoneyTransactionSearchParams} from '@accounting/_common/models/money-transaction.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const moneyTransactionsFeatureKey = 'moneyTransactions';

export function sortByDate(a: MoneyTransaction, b: MoneyTransaction) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<MoneyTransaction> = createEntityAdapter<MoneyTransaction>({
    sortComparer: sortByDate
});

export const typedListInitialState: EntityStateModel<MoneyTransaction, MoneyTransactionSearchParams> = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export interface MoneyTransactionsState {
    bank: EntityStateModel<MoneyTransaction, MoneyTransactionSearchParams>,
    cash: EntityStateModel<MoneyTransaction, MoneyTransactionSearchParams>,
}

export const initialState: MoneyTransactionsState = {
    bank: {...typedListInitialState},
    cash: {...typedListInitialState},
};

export const moneyTransactionsReducer = createReducer(
    initialState,

    on(MoneyTransactionsAction.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return ({
                ...state,
                [action.payload.transactionAccountType]: adapter.removeAll({
                    ...state[action.payload.transactionAccountType],
                    savedFilter: !action.payload.close ? state[action.payload.transactionAccountType].filter : undefined,
                    pagination: state[action.payload.transactionAccountType].pagination,
                    filter: action.payload.close ? state[action.payload.transactionAccountType].savedFilter : undefined
                })
            });
        } else {
            if (action.payload.close) {
                return ({
                    ...state,
                    [action.payload.transactionAccountType]: adapter.setAll(action.payload.items, {
                        ...state[action.payload.transactionAccountType],
                        pagination: {...state[action.payload.transactionAccountType].pagination, number: 0},
                        componentOpened: false
                    })
                });
            }

            return {...state, [action.payload.transactionAccountType]: {...state[action.payload.transactionAccountType], componentOpened: true}};
        }
    }),

    on(MoneyTransactionsAction.LoadList, (state, action) => {
        return ({
            ...state,
            [action.payload.transactionAccountType]: {
                ...state[action.payload.transactionAccountType],
                listLoading: StoredComponentHelperService.updateListLoading(action.payload, state[action.payload.transactionAccountType].ids)
            }
        });
    }),

    on(MoneyTransactionsAction.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.transactionAccountType]: action.payload.clearState
                ? adapter.setAll(action.payload.items, {
                    ...state[action.payload.transactionAccountType],
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : adapter.upsertMany(action.payload.items, {
                    ...state[action.payload.transactionAccountType],
                    pagination: action.payload.pagination,
                    listLoading: undefined
                }),
        });
    }),

    on(MoneyTransactionsAction.LoadListFailure, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            listLoading: undefined
        }
    })),

    on(MoneyTransactionsAction.SetPagination, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            pagination: {
                ...state[action.payload.transactionAccountType].pagination,
                number: action.payload.next ? StoredComponentHelperService.setNextPage(state[action.payload.transactionAccountType].pagination, state[action.payload.transactionAccountType].ids?.length) : 0
            }
        }
    })),

    on(MoneyTransactionsAction.SetFilter, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            filter: action.payload.filter
        }
    })),

    on(MoneyTransactionsAction.SetLoading, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            loading: action.payload.loading
        }
    })),

    on(MoneyTransactionsAction.SetItemsLoading, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.transactionAccountType].itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    on(MoneyTransactionsAction.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state[action.payload.transactionAccountType]?.filter, action.payload.item);
        if (savedItemAvailableByFilter) {
            return {
                ...state,
                [action.payload.transactionAccountType]: adapter.setOne(action.payload.item, {
                    ...state[action.payload.transactionAccountType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.transactionAccountType].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.transactionAccountType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.transactionAccountType].pagination, state[action.payload.transactionAccountType]?.ids)
                    }
                })
            };
        } else {
            return ({
                ...state,
                [action.payload.transactionAccountType]: adapter.removeOne(action.payload.item.id!, {
                    ...state[action.payload.transactionAccountType],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.transactionAccountType].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state[action.payload.transactionAccountType].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.transactionAccountType].pagination, state[action.payload.transactionAccountType]?.ids, true)
                    }
                })
            });
        }
    }),

    on(MoneyTransactionsAction.DeleteItem, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.transactionAccountType].itemsLoading, action.payload.item.id!, true)
        }
    })),

    on(MoneyTransactionsAction.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.transactionAccountType]: adapter.removeOne(action.payload.item.id!, {
                ...state[action.payload.transactionAccountType],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.transactionAccountType].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state[action.payload.transactionAccountType].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state[action.payload.transactionAccountType].pagination, state[action.payload.transactionAccountType]?.ids, true)
                }
            })
        });
    }),

    on(MoneyTransactionsAction.DeleteItemFailure, (state, action) => ({
        ...state,
        [action.payload.transactionAccountType]: {
            ...state[action.payload.transactionAccountType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.transactionAccountType].itemsLoading, action.payload.item.id!, false)
        }
    })),

    on(MoneyTransactionsAction.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (type: MoneyTransactionAccountType) => adapter.getSelectors((state: MoneyTransactionsState) => state[type]).selectAll;

export const isAvailableByFilter = (searchParams: MoneyTransactionSearchParams | undefined, item: MoneyTransaction): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.query && (item.number && item.number.indexOf(searchParams.query) == -1 && item.comment && item.comment.indexOf(searchParams.query) == -1 && (item.amount && parseInt(searchParams.query) != parseInt(item.amount)))) {
        return false;
    }

    if (searchParams.moneyTransactionType && searchParams.moneyTransactionType != item.type) {
        return false;
    }

    if (searchParams.direction && searchParams.direction != item.direction) {
        return false;
    }

    if (searchParams.creationType && searchParams.creationType == 'SYNCED' && !item.synced) {
        return false;
    }

    if (searchParams.creationType && searchParams.creationType == 'MANUAL' && item.synced) {
        return false;
    }

    if (searchParams.analytics?.id) {
        if (searchParams.moneyTransactionType == 'COMPANY') {
            if (item.company?.id != searchParams.analytics?.id) {
                return false;
            }
        }

        if (searchParams.moneyTransactionType == 'APARTMENT' || searchParams.moneyTransactionType == 'SALARY' || searchParams.moneyTransactionType == 'TAX' || searchParams.moneyTransactionType == 'PERSON') {
            if (item.transactionData?.length) {
                if (!item.transactionData.find(el => el.item?.id == searchParams.analytics?.id)) {
                    return false;
                }
            }
        }
    }

    if (searchParams.moneyAccounts?.length && item.account?.id && !searchParams.moneyAccounts.find(el => el.id == item.account?.id)) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.date))) {
        return false;
    }

    if (searchParams.to && dayjs(item.date).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    return !((searchParams.operative || searchParams.operative === false) && searchParams.operative !== item.operative);
};
